import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
  RegionalPricing, 
  CreateRegionalPricingDto, 
  UpdateRegionalPricingDto,
  RegionalPricingResponse,
  RegionalPricingBulkCreateDto,
  RegionalPricingBulkUpdateDto
} from "../../modules/shared/model";
import { 
  createRegionalPricing as createRegionalPricingAction, 
  updateRegionalPricing as updateRegionalPricingAction, 
  getRegionalPricingById as getRegionalPricingByIdAction, 
  getRegionalPricings as getRegionalPricingsAction, 
  deleteRegionalPricing as deleteRegionalPricingAction,
  bulkCreateRegionalPricings as bulkCreateRegionalPricingsAction,
  bulkUpdateRegionalPricings as bulkUpdateRegionalPricingsAction,
  bulkDeleteRegionalPricings as bulkDeleteRegionalPricingsAction
} from "../Actions/RegionalPricingActions";

// Regional Pricing Async Thunks
export const createRegionalPricing = createAsyncThunk("regionalPricings/createRegionalPricing", async (data: CreateRegionalPricingDto, { rejectWithValue }) => {
  try { return await createRegionalPricingAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to create regional pricing"); }
});

export const updateRegionalPricing = createAsyncThunk("regionalPricings/updateRegionalPricing", async ({ id, data }: { id: string; data: UpdateRegionalPricingDto }, { rejectWithValue }) => {
  try { return await updateRegionalPricingAction(id, data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to update regional pricing"); }
});

export const fetchRegionalPricing = createAsyncThunk("regionalPricings/getRegionalPricingById", async (id: string, { rejectWithValue }) => {
  try { return await getRegionalPricingByIdAction(id); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch regional pricing"); }
});

export const fetchRegionalPricings = createAsyncThunk("regionalPricings/getRegionalPricings", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
  try { return await getRegionalPricingsAction(query); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch regional pricings"); }
});

export const deleteRegionalPricing = createAsyncThunk("regionalPricings/deleteRegionalPricing", async (id: string, { rejectWithValue }) => {
  try { await deleteRegionalPricingAction(id); return id; } catch (e: any) { return rejectWithValue(e?.message || "Failed to delete regional pricing"); }
});

export const bulkCreateRegionalPricings = createAsyncThunk("regionalPricings/bulkCreateRegionalPricings", async (data: RegionalPricingBulkCreateDto, { rejectWithValue }) => {
  try { return await bulkCreateRegionalPricingsAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to bulk create regional pricings"); }
});

export const bulkUpdateRegionalPricings = createAsyncThunk("regionalPricings/bulkUpdateRegionalPricings", async (data: RegionalPricingBulkUpdateDto, { rejectWithValue }) => {
  try { return await bulkUpdateRegionalPricingsAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to bulk update regional pricings"); }
});

export const bulkDeleteRegionalPricings = createAsyncThunk("regionalPricings/bulkDeleteRegionalPricings", async (ids: string[], { rejectWithValue }) => {
  try { await bulkDeleteRegionalPricingsAction(ids); return ids; } catch (e: any) { return rejectWithValue(e?.message || "Failed to bulk delete regional pricings"); }
});

interface RegionalPricingState {
  regionalPricings: RegionalPricing[];
  currentRegionalPricing: RegionalPricing | null;
  loading: boolean;
  error: string | null;
  totalCount?: number;
  hasNextPage?: boolean;
}

const initialState: RegionalPricingState = {
  regionalPricings: [],
  currentRegionalPricing: null,
  loading: false,
  error: null,
  totalCount: 0,
  hasNextPage: false,
};

const regionalPricingSlice = createSlice({
  name: "regionalPricings",
  initialState,
  reducers: {
    clearError(state) { 
      state.error = null; 
    },
    clearCurrentRegionalPricing(state) { 
      state.currentRegionalPricing = null; 
    },
    clearRegionalPricings(state) {
      state.regionalPricings = [];
      state.currentRegionalPricing = null;
      state.totalCount = 0;
      state.hasNextPage = false;
    },
    setCurrentRegionalPricing(state, action: PayloadAction<RegionalPricing>) {
      state.currentRegionalPricing = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Create Regional Pricing
    builder
      .addCase(createRegionalPricing.pending, (s) => { 
        s.loading = true; 
        s.error = null; 
      })
      .addCase(createRegionalPricing.fulfilled, (s, a: PayloadAction<RegionalPricing>) => { 
        s.loading = false; 
        s.regionalPricings.unshift(a.payload); 
        s.totalCount = (s.totalCount || 0) + 1; 
      })
      .addCase(createRegionalPricing.rejected, (s, a: any) => { 
        s.loading = false; 
        s.error = a.payload as string; 
      });

    // Update Regional Pricing
    builder
      .addCase(updateRegionalPricing.pending, (s) => { 
        s.loading = true; 
        s.error = null; 
      })
      .addCase(updateRegionalPricing.fulfilled, (s, a: PayloadAction<RegionalPricing>) => {
        s.loading = false;
        const i = s.regionalPricings.findIndex(x => x.id === a.payload.id);
        if (i !== -1) s.regionalPricings[i] = a.payload;
        if (s.currentRegionalPricing?.id === a.payload.id) s.currentRegionalPricing = a.payload;
      })
      .addCase(updateRegionalPricing.rejected, (s, a: any) => { 
        s.loading = false; 
        s.error = a.payload as string; 
      });

    // Fetch Single Regional Pricing
    builder
      .addCase(fetchRegionalPricing.pending, (s) => { 
        s.loading = true; 
        s.error = null; 
      })
      .addCase(fetchRegionalPricing.fulfilled, (s, a: PayloadAction<RegionalPricing>) => { 
        s.loading = false; 
        s.currentRegionalPricing = a.payload; 
      })
      .addCase(fetchRegionalPricing.rejected, (s, a: any) => { 
        s.loading = false; 
        s.error = a.payload as string; 
      });

    // Fetch All Regional Pricings
    builder
      .addCase(fetchRegionalPricings.pending, (s) => { 
        s.loading = true; 
        s.error = null; 
      })
      .addCase(fetchRegionalPricings.fulfilled, (s, a: PayloadAction<RegionalPricingResponse>) => { 
        s.loading = false; 
        s.regionalPricings = a.payload.data || []; 
        s.totalCount = a.payload.totalCount || 0; 
        s.hasNextPage = a.payload.hasNextPage || false;
      })
      .addCase(fetchRegionalPricings.rejected, (s, a: any) => { 
        s.loading = false; 
        s.error = a.payload as string; 
      });

    // Delete Regional Pricing
    builder
      .addCase(deleteRegionalPricing.pending, (s) => { 
        s.loading = true; 
        s.error = null; 
      })
      .addCase(deleteRegionalPricing.fulfilled, (s, a: PayloadAction<string>) => { 
        s.loading = false; 
        s.regionalPricings = s.regionalPricings.filter(x => x.id !== a.payload); 
        if (s.currentRegionalPricing?.id === a.payload) s.currentRegionalPricing = null; 
        s.totalCount = Math.max(0, (s.totalCount || 0) - 1); 
      })
      .addCase(deleteRegionalPricing.rejected, (s, a: any) => { 
        s.loading = false; 
        s.error = a.payload as string; 
      });

    // Bulk Create Regional Pricings
    builder
      .addCase(bulkCreateRegionalPricings.pending, (s) => { 
        s.loading = true; 
        s.error = null; 
      })
      .addCase(bulkCreateRegionalPricings.fulfilled, (s, a: PayloadAction<RegionalPricing[]>) => { 
        s.loading = false; 
        s.regionalPricings.unshift(...a.payload); 
        s.totalCount = (s.totalCount || 0) + a.payload.length; 
      })
      .addCase(bulkCreateRegionalPricings.rejected, (s, a: any) => { 
        s.loading = false; 
        s.error = a.payload as string; 
      });

    // Bulk Update Regional Pricings
    builder
      .addCase(bulkUpdateRegionalPricings.pending, (s) => { 
        s.loading = true; 
        s.error = null; 
      })
      .addCase(bulkUpdateRegionalPricings.fulfilled, (s, a: PayloadAction<RegionalPricing[]>) => { 
        s.loading = false; 
        a.payload.forEach(updatedPricing => {
          const i = s.regionalPricings.findIndex(x => x.id === updatedPricing.id);
          if (i !== -1) s.regionalPricings[i] = updatedPricing;
          if (s.currentRegionalPricing?.id === updatedPricing.id) s.currentRegionalPricing = updatedPricing;
        });
      })
      .addCase(bulkUpdateRegionalPricings.rejected, (s, a: any) => { 
        s.loading = false; 
        s.error = a.payload as string; 
      });

    // Bulk Delete Regional Pricings
    builder
      .addCase(bulkDeleteRegionalPricings.pending, (s) => { 
        s.loading = true; 
        s.error = null; 
      })
      .addCase(bulkDeleteRegionalPricings.fulfilled, (s, a: PayloadAction<string[]>) => { 
        s.loading = false; 
        s.regionalPricings = s.regionalPricings.filter(x => !a.payload.includes(x.id)); 
        if (s.currentRegionalPricing && a.payload.includes(s.currentRegionalPricing.id)) s.currentRegionalPricing = null; 
        s.totalCount = Math.max(0, (s.totalCount || 0) - a.payload.length); 
      })
      .addCase(bulkDeleteRegionalPricings.rejected, (s, a: any) => { 
        s.loading = false; 
        s.error = a.payload as string; 
      });
  }
});

export const { 
  clearError, 
  clearCurrentRegionalPricing, 
  clearRegionalPricings,
  setCurrentRegionalPricing
} = regionalPricingSlice.actions;
export default regionalPricingSlice.reducer;
